import { useState } from "react";
import { useParams } from "react-router-dom";
import { UserCheck, Check, X, MessageSquare, Hand } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Status, Info, History, ActionButton, RunButton, BackLink, useLoad, fmt, deText,
} from "@/components/control/kit";

function Decision({ d, id, reload }) {
  const [checks, setChecks] = useState({});
  const all = d.checks.every((c) => checks[c.key]);
  const r = d.review;
  const scope = deText(r.type);
  return (
    <Section title="Prüfung und Entscheidung" testId="review-decision">
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium text-zinc-800">Prüfpunkte</legend>
        {d.checks.map((c) => (
          <label key={c.key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!checks[c.key]} onChange={(e) => setChecks((s) => ({ ...s, [c.key]: e.target.checked }))}
              data-testid={`review-check-${c.key}`} className="h-4 w-4 accent-[#D50B58]" /> {c.label}
          </label>
        ))}
      </fieldset>
      <div className="mt-4 flex flex-wrap gap-2">
        <ActionButton label="Freigeben" icon={Check} variant="default" testId="approve-review" disabled={!all} onDone={reload}
          page="freigabe" pageSubtitle="Die Freigabe betrifft das angezeigte öffentliche Profil." pageSubject={r.member_name}
          pageRows={[["Mitglied", r.member_name], ["Prüfumfang", scope],
            ["Ergebnis", all ? "Alle Prüfpunkte bestätigt · zur Freigabe vorgelegt" : "Prüfpunkte noch nicht bestätigt"],
            ["Privater Access Pass", "Nicht im Prüfumfang"]]}
          pageNotice={{ title: "Wirkung nach Bestätigung", text: "Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen.", tone: "lavender" }}
          title="Profilfreigabe bestätigen" submitLabel="Jetzt freigeben"
          description="Das Profil wird sofort in der Mitglieder-App sichtbar. Das Mitglied erhält eine Benachrichtigung."
          fields={[{ type: "info", text: all ? "Alle Prüfpunkte wurden bestätigt."
            : "Noch nicht alle Prüfpunkte sind bestätigt. Gehe zurück zur Prüfung und bestätige jeden Prüfpunkt." }]}
          onSubmit={async () => {
            if (!all) throw Object.assign(new Error(), { response: { status: 409, data: { detail: "Freigabe erst nach allen bestätigten Prüfpunkten möglich." } } });
            await api.post(`/reviews/${id}/approve`, { checks });
            return "Profil freigegeben.";
          }} />
        <ActionButton label="Rückfrage" icon={MessageSquare} testId="request-changes" onDone={reload} title="Rückfrage zum Profil formulieren"
          page="rueckfrage" pageSubtitle="Die Rückfrage wird als sichtbare Nachricht an das Mitglied formuliert." pageSubject={r.member_name}
          pageRows={[["Empfänger", r.member_name], ["Bezug", scope], ["Versandstatus", "Entwurf · noch nicht gesendet"]]}
          submitLabel="Rückfrage senden" description="Das Mitglied sieht nur den Text für das Mitglied. Nach der Überarbeitung kehrt das Profil in die Prüfung zurück."
          fields={[
            { name: "message", label: "Text für das Mitglied", type: "textarea", required: true },
            { name: "internal_note", label: "Interne Notiz", type: "textarea" },
          ]}
          preview={(v) => (
            <div className="rounded-lg border border-brand-purple/20 bg-secondary/60 p-3" data-testid="request-changes-preview" aria-live="polite">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Vorschau für das Mitglied (Benachrichtigung und Profilstatus)</p>
              <p className="mt-1 text-sm font-semibold text-brand-purple">Rückfrage zu deinem Profil</p>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-800">{(v.message || "").trim() || "Noch kein Text eingegeben."}</p>
              <p className="mt-2 text-xs text-muted-foreground">Die interne Notiz wird dem Mitglied nicht angezeigt.</p>
            </div>
          )}
          onSubmit={async (v) => { await api.post(`/reviews/${id}/request-changes`, v); return "Rückfrage gesendet."; }} />
        <ActionButton label="Ablehnen" icon={X} testId="reject-review" danger onDone={reload} title="Profil ablehnen und begründen"
          page="ablehnen" pageSubtitle="Formuliere eine sachliche Begründung und eine verständliche Rückmeldung." pageSubject={r.member_name}
          pageRows={[["Mitglied", r.member_name], ["Bezug", scope], ["Entscheidung", "Ablehnung zur Prüfung"]]}
          pageNotice={{ title: "Entscheidung noch offen", text: "Begründung und Nachricht vor einer bestätigten Massnahme prüfen." }}
          submitLabel="Ablehnen"
          fields={[
            { name: "reason", label: "Ablehnungsgrund (intern)", type: "select", required: true, options: [
              "Community-Regeln verletzt", "Kontaktdaten im öffentlichen Text", "Unangemessenes Bild", "Angaben unvollständig", "Sonstiges",
            ].map((x) => ({ value: x, label: x })) },
            { name: "message", label: "Text für das Mitglied", type: "textarea", required: true },
            { name: "internal_note", label: "Interne Notiz", type: "textarea" },
          ]}
          onSubmit={async (v) => { await api.post(`/reviews/${id}/reject`, v); return "Profil abgelehnt."; }} />
      </div>
      {!all && <p className="mt-2 text-xs text-muted-foreground">Freigabe erst möglich, wenn alle Prüfpunkte bestätigt sind.</p>}
    </Section>
  );
}

function ReviewView({ d, id, reload }) {
  const r = d.review;
  const p = d.public_profile;
  return (
    <>
      <PageHeader title={r.member_name} subtitle={`${deText(r.type)} · eingereicht ${fmt(r.submitted_at)}`} icon={UserCheck} actions={<>
        <Status value={r.status} testId="review-status" />
        {r.status === "offen" && d.can_decide && !r.assigned_to && (
          <RunButton label="Mir zuweisen" icon={Hand} testId="review-claim" success="Prüfung übernommen." onDone={reload}
            run={() => api.post(`/tasks/review/${id}/assign`, {})} />
        )}
      </>} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Öffentliches Profil" testId="review-public-profile">
          <Info items={[["Ort", p.city], ["Organisation", p.organization], ["Sprachen", (p.languages || []).join(", ")],
            ["Interessen", (p.interests || []).map(deText).join(", ")], ["Über mich", deText(p.bio)],
            ["Hinweis der einreichenden Person", deText(r.note)],
            r.changed_fields?.length && ["Geänderte Felder (erneute Prüfung)", r.changed_fields.join(", ")],
            r.visible_message && ["Letzte Nachricht an das Mitglied", r.visible_message],
            r.assigned_to && ["Zuständig", r.assigned_to.name]]} />
        </Section>
        {r.status === "offen" && d.can_decide ? <Decision d={d} id={id} reload={reload} /> : (
          <Section title="Entscheidung" testId="review-result">
            <Info items={[["Status", <Status value={r.status} />], ["Entschieden am", fmt(r.decided_at || r.asked_at)],
              ["Durch", r.decided_by], r.reject_reason && ["Grund", r.reject_reason]]} />
          </Section>
        )}
      </div>
      <Section title="Verlauf" className="mt-5" testId="review-history"><History items={r.history} /></Section>
    </>
  );
}

export default function ReviewDetail() {
  const { id } = useParams();
  const st = useLoad(`/reviews/${id}`);
  return (
    <div>
      <BackLink to="/profilpruefungen">Zurück zu Profilprüfungen</BackLink>
      <Loadable state={st}>{(d) => <ReviewView d={d} id={id} reload={st.reload} />}</Loadable>
    </div>
  );
}
